/* eslint-disable */
// Using a angular-oauth2-oidc library to support OAuth 2 and OpenId Connect (OIDC) in Angular
// https://manfredsteyer.github.io/angular-oauth2-oidc/
// --------------------------------------------------------------------

import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { UserClaimAdapter } from '@challenger/care/models/user-claim-adapter.model';
import { UserToken } from '@challenger/care/models/user-token.model';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { UserFacade } from '@challenger/care/modules/core-feature-store/facades/user.facade';
import { LIB_CONFIG } from '@challenger/care/lib-config';
import { LibConfig } from '@challenger/care/models/lib-config.model';
import { TokenService } from '@challenger/care/services/token.service';
import { UtilityService } from '@challenger/care/services/utility.service';
import { mergeMap } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private userFacade: UserFacade,
    private oauthService: OAuthService,
    private router: Router,
    private utilityService: UtilityService,
    public zone: NgZone,
    private userClaimAdapter: UserClaimAdapter,
    @Inject(LIB_CONFIG) private config: LibConfig,
    private tokenService: TokenService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    // // Useful for debugging:
    // this.oauthService.events.subscribe((event) => {
    //   if (event instanceof OAuthErrorEvent) {
    //     console.error(event);
    //   } else {
    //     console.warn(event);
    //   }
    // });
    if (isPlatformBrowser(this.platformId)) {
      this.oauthService.events.subscribe((event) => {
        const errorResponsesRequiringUserInteraction = [
          'interaction_required',
          'login_required',
          'account_selection_required',
          'consent_required',
        ];
        if (event instanceof OAuthErrorEvent) {
          if (
            event.type == 'code_error' &&
            event.params &&
            errorResponsesRequiringUserInteraction.indexOf(event.params['error'])
          ) {
            this.zone.run(() => {
              sessionStorage.removeItem('id_token');
              sessionStorage.removeItem('access_token');
              this.router.navigate(['/my-virgin-media/']);
            });
          }
        } else {
          if (event.type === 'token_expires') {
            this.zone.run(() => {
              sessionStorage.removeItem('id_token');
              sessionStorage.removeItem('access_token');
              this.router.navigate(['/my-virgin-media/']);
            });
          }
        }
      });
    }
  }

  private isSilentRefreshON = false;

  initialise() {
    const url: string = this.utilityService.getNestedObject(this.oauthService, ['document', 'URL']);
    if (url && url.includes(`${this.config?.authConfig?.redirectUri}?code=`)) {
      this.userFacade.authenticatingUser(true);
    } else {
      this.userFacade.authenticatingUser(false);
    }

    this.oauthService.configure(this.config.authConfig);

    this.oauthService.loadDiscoveryDocumentAndTryLogin().then((result) => {
      if (result) {
        if (this.isAuthenticated()) {
          this.tokenService
            .getAuthToken()
            .pipe(
              mergeMap((authToken: any) => {
                return this.tokenService.getRegToken(authToken).pipe(
                  mergeMap((regToken: any) => {
                    return regToken;
                  })
                );
              })
            )
            .subscribe();

          this.setUserProfile();
          const returnUrl = decodeURIComponent(this.oauthService.state);

          // Start token refresh
          if (!this.isSilentRefreshON && this.config.authConfig.useSilentRefresh) {
            this.isSilentRefreshON = true;
            this.oauthService.stopAutomaticRefresh();
            this.oauthService.setupAutomaticSilentRefresh();
          }

          if (returnUrl) {
            this.zone.run(() => {
              this.router.navigateByUrl(returnUrl);
            });
          }
        }
      }
    });
  }

  setUserProfile() {
    const accessToken: UserToken = {
      value: this.oauthService.getAccessToken(),
      expiry: this.oauthService.getAccessTokenExpiration(),
    };
    const idToken: UserToken = {
      value: this.oauthService.getIdToken(),
      expiry: this.oauthService.getIdTokenExpiration(),
    };
    const claims = this.oauthService.getIdentityClaims();
    if (claims) {
      this.userFacade.setUserDetail(this.userClaimAdapter.adapt(claims), accessToken, idToken);
    }
  }

  displayName(): string {
    return this.getIdentityClaimProperty('displayName');
  }

  getIdentityClaimProperty(name: string): string {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) {
      return null;
    }
    /* eslint-disable @typescript-eslint/dot-notation */
    return claims[name];
  }

  get idToken(): string {
    return this.oauthService.getIdToken();
  }

  get accessToken(): string {
    return this.oauthService.getAccessToken();
  }

  get oauthEvents(): any {
    return this.oauthService.events;
  }

  isAuthenticated() {
    return (
      this.oauthService.getIdToken() !== null &&
      this.oauthService.getAccessToken() !== null &&
      this.oauthService.hasValidAccessToken()
    );
  }

  reLogin(returnUrl?: string) {
    this.oauthService.resetImplicitFlow();
    this.redirectToLogin(returnUrl);
  }

  redirectToLogin(returnUrl?: string) {
    if (returnUrl && returnUrl.includes('?code=')) {
      return;
    }    

    if (window.location.pathname !== '/help/how-to/auth-deflection') {
      sessionStorage.setItem('vmo2_seo_returnUrl', window.location.pathname);
    }
    
    this.oauthService.initImplicitFlow(returnUrl);
  }

  logout(logoutRedirectUrl?: string) {
    const logoutUrl = `${this.config.authConfig?.issuer}/idp/startSLO.ping?TargetResource=${encodeURIComponent(
      logoutRedirectUrl ? logoutRedirectUrl : this.config.authConfig?.redirectUri
    )}`;

    this.oauthService.logOut();
    window.location.href = logoutUrl;
  }
}
